import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import type { LlmTool } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToolCategories } from "./LlmToolsList";

interface FormState {
  toolId: string;
  name: string;
  description: string;
  category: string;
  isActive: boolean;
  sortOrder: number;
  specJson: string;
}

const EMPTY: FormState = {
  toolId: "",
  name: "",
  description: "",
  category: "",
  isActive: true,
  sortOrder: 0,
  specJson: "{}",
};

/**
 * Mirrors backend/src/database/migrations/1748400000000-seed-entity-crud-tools.ts.
 * Lets a superadmin pre-fill the form for one of the canonical *_crud tools
 * instead of hand-typing the whole spec.
 */
const CRUD_CATEGORIES = [
  "crm",
  "products",
  "task_tracker",
  "knowledge",
  "email",
  "marketing",
  "integration",
  "ticket",
  "org",
  "portal",
] as const;

type CrudCategory = (typeof CRUD_CATEGORIES)[number];

const CRUD_CATEGORY_DEFAULTS: Record<
  CrudCategory,
  { description: string; sortOrder: number; entities: string[] }
> = {
  crm: {
    description:
      "CRUD over CRM entities (deals, accounts, tasks, pipelines, invoices, resources, etc.). Always call op=describe to learn the field shape before create/update.",
    sortOrder: 100,
    entities: [
      "account",
      "deal",
      "task",
      "activity",
      "pipeline",
      "pipeline_stage",
      "invoice",
      "resource",
      "inventory",
      "location",
      "tag",
      "note",
      "proposal",
      "review",
      "loyalty",
      "message_template",
      "document_template",
      "crm_saved_view",
      "custom_field",
      "crm_webhook",
      "automation_rule",
      "scheduled_action",
      "distribution_config",
      "scoring_rule",
    ],
  },
  products: {
    description:
      "CRUD over product catalog entities (products, categories, special conditions, unavailability scenarios).",
    sortOrder: 110,
    entities: [
      "product",
      "product_category",
      "unavailable_scenario",
      "special_condition",
    ],
  },
  task_tracker: {
    description:
      "CRUD over task-tracker entities (project-management tasks, projects, goals, templates, comments, labels, time entries). Distinct from CRM tasks.",
    sortOrder: 120,
    entities: [
      "tracker_task",
      "tracker_project",
      "project",
      "goal",
      "task_template",
      "task_comment",
      "task_label",
      "task_attachment",
      "time_entry",
    ],
  },
  knowledge: {
    description:
      "CRUD over knowledge-base entities (articles, categories, attachments, subscriptions, webhooks).",
    sortOrder: 130,
    entities: [
      "knowledge_article",
      "knowledge_category",
      "knowledge_attachment",
      "knowledge_subscription",
      "knowledge_webhook",
    ],
  },
  email: {
    description:
      "CRUD over email/contact-center entities (threads, messages, templates, routing rules, sessions, sequences, internal comments, auto-replies, AI drafts).",
    sortOrder: 140,
    entities: [
      "email_thread",
      "email_message",
      "email_template",
      "email_routing_rule",
      "email_session",
      "email_sequence",
      "email_internal_comment",
      "email_auto_reply",
      "email_assignment_config",
      "email_draft",
    ],
  },
  marketing: {
    description:
      "CRUD over marketing entities (campaigns, channels, audiences, UTM links).",
    sortOrder: 150,
    entities: [
      "campaign",
      "marketing_channel",
      "marketing_audience",
      "marketing_utm_link",
    ],
  },
  integration: {
    description: "CRUD over integration entities (integrations, connections).",
    sortOrder: 160,
    entities: ["integration", "integration_connection"],
  },
  ticket: {
    description:
      "CRUD over ticketing entities (tickets, comments, escalation rules).",
    sortOrder: 170,
    entities: ["ticket", "ticket_comment", "ticket_escalation_rule"],
  },
  org: {
    description:
      "CRUD over organization/admin entities (employees, roles, role permissions, invites, employee role assignments, instructions, prompts, departments, tenant info, saved views, files, sessions, session messages, consents).",
    sortOrder: 180,
    entities: [
      "employee",
      "tenant_role",
      "role_permission",
      "tenant_invite",
      "employee_role",
      "employee_instruction",
      "employee_prompt",
      "department",
      "tenant_information",
      "saved_view",
      "file",
      "session",
      "session_message",
      "consent",
    ],
  },
  portal: {
    description: "CRUD over client portal entities (portal access).",
    sortOrder: 190,
    entities: ["portal_access"],
  },
};

/**
 * Union of every entity referenced by any category — used as the option set
 * in the multi-select so a user can mix-and-match across categories if needed.
 */
const ALL_CRUD_ENTITIES: string[] = Array.from(
  new Set(
    CRUD_CATEGORIES.flatMap((c) => CRUD_CATEGORY_DEFAULTS[c].entities)
  )
).sort();

function buildCrudSpec(
  category: CrudCategory,
  entities: string[],
  description: string
): Record<string, unknown> {
  const toolId = `${category}.crud`;
  return {
    toolId,
    name: toolId,
    description,
    category,
    operation: "entity_crud",
    routing: {
      service: "EntityCrudToolService",
      method: "execute",
    },
    parameters: {
      required: ["entity", "op"],
      properties: {
        entity: {
          type: "string",
          enum: entities,
          description: `Which entity to operate on. Allowed: ${entities.join(", ")}.`,
        },
        op: {
          type: "string",
          enum: ["list", "get", "create", "update", "soft_delete", "describe"],
          description:
            "Operation: list (paginated), get (by id), create, update (by id), soft_delete (sets deletedAt; never hard-deletes), describe (returns the entity's writable fields).",
        },
        id: {
          type: "string",
          description: "Entity id (required for get/update/soft_delete).",
        },
        data: {
          type: "object",
          description:
            "Per-entity payload (required for create/update). Call op=describe first to learn the writable field shape.",
          additionalProperties: true,
        },
        filters: {
          type: "object",
          description:
            "Optional equality filters for op=list. Only fields that exist on the entity are honored.",
          additionalProperties: true,
        },
        limit: {
          type: "integer",
          minimum: 1,
          maximum: 50,
          description: "Page size for op=list (default 25).",
        },
        cursor: {
          type: "string",
          description:
            "Opaque pagination cursor returned by the previous list call.",
        },
      },
    },
    _localOnly: true,
  };
}

function dtoToForm(t: LlmTool): FormState {
  return {
    toolId: t.toolId,
    name: t.name,
    description: t.description,
    category: t.category,
    isActive: t.isActive,
    sortOrder: t.sortOrder,
    specJson: JSON.stringify(t.spec, null, 2),
  };
}

export function LlmToolFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation("tools");
  const categories = useToolCategories();

  const { data, isLoading, error } = useQuery({
    queryKey: ["llm-tool", id],
    queryFn: () => api.get<{ tool: LlmTool }>(`/api/admin/llm-tools/${id}`),
    enabled: !isNew,
  });

  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [initialized, setInitialized] = React.useState(false);
  const [scaffoldCategory, setScaffoldCategory] =
    React.useState<CrudCategory>("crm");
  const [scaffoldEntities, setScaffoldEntities] = React.useState<Set<string>>(
    () => new Set(CRUD_CATEGORY_DEFAULTS.crm.entities)
  );

  function onScaffoldCategoryChange(next: CrudCategory) {
    setScaffoldCategory(next);
    setScaffoldEntities(new Set(CRUD_CATEGORY_DEFAULTS[next].entities));
  }

  function toggleScaffoldEntity(entity: string) {
    setScaffoldEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  }

  function applyScaffold() {
    const entities = Array.from(scaffoldEntities);
    if (entities.length === 0) {
      toast.error(t("scaffold.pickEntity"));
      return;
    }
    const defaults = CRUD_CATEGORY_DEFAULTS[scaffoldCategory];
    const description = defaults.description;
    const spec = buildCrudSpec(scaffoldCategory, entities, description);
    setForm((prev) => ({
      ...prev,
      toolId: `${scaffoldCategory}.crud`,
      name: `${scaffoldCategory}.crud`,
      description,
      category: scaffoldCategory,
      sortOrder: defaults.sortOrder,
      specJson: JSON.stringify(spec, null, 2),
    }));
    toast.success(
      t("scaffold.done", { toolId: `${scaffoldCategory}.crud`, count: entities.length })
    );
  }

  React.useEffect(() => {
    if (isNew && !initialized) {
      setForm(EMPTY);
      setInitialized(true);
    }
    if (!isNew && data?.tool && !initialized) {
      setForm(dtoToForm(data.tool));
      setInitialized(true);
    }
  }, [isNew, data, initialized]);

  function parseSpec(): { ok: true; spec: Record<string, unknown> } | { ok: false; error: string } {
    try {
      const parsed = JSON.parse(form.specJson);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return { ok: false, error: t("form.specNotObject") };
      }
      return { ok: true, spec: parsed as Record<string, unknown> };
    } catch (err) {
      return {
        ok: false,
        error: t("form.invalidJson", { message: err instanceof Error ? err.message : String(err) }),
      };
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const parsed = parseSpec();
      if (!parsed.ok) throw new Error(parsed.error);

      const body = {
        toolId: form.toolId.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category.trim(),
        isActive: form.isActive,
        sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
        spec: parsed.spec,
      };

      if (isNew) {
        return api.post<{ tool: LlmTool }>("/api/admin/llm-tools", body);
      }
      return api.put<{ tool: LlmTool }>(`/api/admin/llm-tools/${id}`, body);
    },
    onSuccess: (res) => {
      toast.success(isNew ? t("form.created") : t("form.saved"));
      qc.invalidateQueries({ queryKey: ["llm-tools"] });
      qc.invalidateQueries({ queryKey: ["llm-tools-summaries"] });
      qc.setQueryData(["llm-tool", res.tool.id], res);
      if (isNew) navigate(`/tools/${res.tool.id}`, { replace: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : String(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      api.del<{ ok: true; toolId: string; templatesUpdated: number }>(
        `/api/admin/llm-tools/${id}`
      ),
    onSuccess: (res) => {
      const swept = res.templatesUpdated;
      toast.success(
        swept > 0
          ? t("form.deletedWithTemplates", { count: swept })
          : t("form.deleted")
      );
      qc.invalidateQueries({ queryKey: ["llm-tools"] });
      qc.invalidateQueries({ queryKey: ["llm-tools-summaries"] });
      qc.invalidateQueries({ queryKey: ["employee-templates"] });
      qc.invalidateQueries({ queryKey: ["employee-template"] });
      navigate("/tools", { replace: true });
    },
    onError: (err) => {
      toast.error(
        t("form.deleteFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  function handleDelete() {
    if (!data?.tool) return;
    if (
      !window.confirm(
        t("form.confirmDelete", { toolId: data.tool.toolId })
      )
    ) {
      return;
    }
    deleteMutation.mutate();
  }

  if (!isNew && isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }
  if (!isNew && error) {
    return (
      <p className="text-sm text-destructive">
        {t("errors.loadFailed", {
          message: error instanceof Error ? error.message : String(error),
        })}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/tools" className="hover:underline">{t("list.title")}</Link>
        <span>/</span>
        <span>{isNew ? t("form.newTool") : data?.tool?.toolId ?? t("form.edit")}</span>
      </div>

      {isNew && (
        <Card>
          <CardHeader>
            <CardTitle>{t("scaffold.title")}</CardTitle>
            <CardDescription>
              {t("scaffold.descriptionPrefix")} <code>&lt;category&gt;.crud</code>{" "}
              {t("scaffold.descriptionBackedBy")} <code>EntityCrudToolService.execute</code>
              {t("scaffold.descriptionSuffix")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="scaffoldCategory">{t("scaffold.category")}</Label>
                <select
                  id="scaffoldCategory"
                  value={scaffoldCategory}
                  onChange={(e) =>
                    onScaffoldCategoryChange(e.target.value as CrudCategory)
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {CRUD_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  {t("scaffold.categoryHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t("scaffold.selected")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("scaffold.selectedCount", { count: scaffoldEntities.size })}
                </p>
                <Button type="button" onClick={applyScaffold}>
                  {t("scaffold.apply")}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("scaffold.entities")}</Label>
              <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-3">
                {ALL_CRUD_ENTITIES.map((entity) => {
                  const inDefault =
                    CRUD_CATEGORY_DEFAULTS[scaffoldCategory].entities.includes(
                      entity
                    );
                  return (
                    <label
                      key={entity}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={scaffoldEntities.has(entity)}
                        onChange={() => toggleScaffoldEntity(entity)}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span
                        className={
                          inDefault ? "" : "text-muted-foreground"
                        }
                      >
                        {entity}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("scaffold.entitiesHint")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {isNew ? t("form.newTool") : t("form.editTitle", { toolId: data?.tool?.toolId })}
          </CardTitle>
          <CardDescription>
            {t("form.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="toolId">tool_id</Label>
              <Input
                id="toolId"
                value={form.toolId}
                onChange={(e) => setForm({ ...form, toolId: e.target.value })}
                placeholder={t("form.toolIdPlaceholder")}
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t("form.name")}</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("form.namePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">{t("form.category")}</Label>
              <Input
                id="category"
                list="category-options"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder={t("form.categoryPlaceholder")}
              />
              <datalist id="category-options">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground">
                {t("form.categoryHint")}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sortOrder">{t("form.sortOrder")}</Label>
              <Input
                id="sortOrder"
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t("form.descriptionLabel")}</Label>
            <Textarea
              id="description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("form.descriptionPlaceholder")}
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="isActive"
              checked={form.isActive}
              onCheckedChange={(v) => setForm({ ...form, isActive: v })}
            />
            <Label htmlFor="isActive">{t("form.active")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("form.activeHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="spec">{t("form.spec")}</Label>
            <Textarea
              id="spec"
              rows={16}
              value={form.specJson}
              onChange={(e) => setForm({ ...form, specJson: e.target.value })}
              className="font-mono text-xs"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              {t("form.specHint")}
            </p>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? t("form.saving") : isNew ? t("form.create") : t("form.save")}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/tools">{t("form.cancel")}</Link>
              </Button>
            </div>
            {!isNew && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
                <span>{deleteMutation.isPending ? t("form.deleting") : t("form.delete")}</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
